/**
 * 管理后台：用户管理
 */
import { useState } from 'react';
import { Modal, Form } from 'antd';
import { message } from '../utils/antdMessage';
import { normalizeSystemRole, resolveDisplayRole, resolveRoleFromGroupNames } from '../utils/systemRoles';

export default function useAdminUsers({ apiRequest }) {
  const [users, setUsers] = useState([]);
  const [userForm] = Form.useForm();
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserKeys, setSelectedUserKeys] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [resetPwd, setResetPwd] = useState('');
  const [brandingScopeModalVisible, setBrandingScopeModalVisible] = useState(false);
  const [brandingScopeUserId, setBrandingScopeUserId] = useState(null);
  const [brandingScopeUserName, setBrandingScopeUserName] = useState('');

  const fetchUsers = async () => {
    // 拉取用户及其分组
    const data = await apiRequest('/api/admin/users');
    if (data.success) {
      // 新增：为每个用户查询分组
      const usersWithGroups = await Promise.all(data.users.map(async user => {
        const groupRes = await apiRequest(`/api/admin/user_groups/${user.id}`);
        return { ...user, groups: groupRes.success ? groupRes.groups : [] };
      }));

      // 按分组纠正系统角色：仅在管理员组却标成 admin 的账号 → manager
      let healedCount = 0;
      const syncedUsers = await Promise.all(usersWithGroups.map(async (user) => {
        const expected = resolveRoleFromGroupNames((user.groups || []).map((g) => g.name));
        const current = normalizeSystemRole(user.role);
        if (expected === current) return user;
        try {
          const res = await apiRequest('/api/admin/users/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: user.id, role: expected })
          });
          if (res?.success) {
            healedCount += 1;
            return { ...user, role: expected };
          }
        } catch (error) {
          console.warn('同步系统角色失败:', user.username, error);
        }
        return { ...user, role: expected };
      }));

      setUsers(syncedUsers);
      if (healedCount > 0) {
        message.info(`已按分组纠正 ${healedCount} 个账号的系统角色（如管理员组→manager）`);
      }
    }
  };
  const handleSaveUser = async (values) => {
    let url, method;
    if (editingUser) {
      url = '/api/admin/users/update';
      method = 'POST';
      // 编辑用户时，添加用户ID
      values.id = editingUser.id;
    } else {
      url = '/api/admin/users/add';
      method = 'POST';
    }
    const data = await apiRequest(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
    if (data.success) {
      message.success('保存成功');
      setUserModalVisible(false);
      setEditingUser(null);
      userForm.resetFields();
      fetchUsers();
    } else {
      message.error((data.error || '保存失败') + (data.detail ? `（${data.detail}）` : '') + '。若提示参数绑定错误，请重启本机后端 :5000 后再保存所属组织。');
    }
  };
  const handleDeleteUser = async (id) => {
    Modal.confirm({
      title: '确定要删除该用户吗？',
      onOk: async () => {
        const data = await apiRequest('/api/admin/users/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
        if (data.success) fetchUsers();
        else message.error(data.error || '删除失败');
      }
    });
  };
  const openBrandingScopeModal = (user) => {
    setBrandingScopeUserId(user?.id ?? null);
    setBrandingScopeUserName(user?.username || user?.real_name || '');
    setBrandingScopeModalVisible(true);
  };
  const closeBrandingScopeModal = () => {
    setBrandingScopeModalVisible(false);
    setBrandingScopeUserId(null);
    setBrandingScopeUserName('');
  };

  const handleAddUser = () => {
    setEditingUser(null);
    userForm.resetFields();
    setUserModalVisible(true);
  };
  const handleEditUser = (user) => {
    setEditingUser(user);
    // 设置表单初始值，确保字段名匹配；角色按分组推导（管理员组→manager）
    userForm.setFieldsValue({
      username: user.username,
      realName: user.real_name, // 数据库字段名转换为表单字段名
      email: user.email,
      role: resolveDisplayRole(user),
      org_name: user.org_name || undefined
    });
    setUserModalVisible(true);
  };
  // 生成随机8位密码
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleResetPwd = async (id) => {
    // 生成随机8位密码
    const newPassword = generateRandomPassword();
    setResetPwd(newPassword);
    
    const data = await apiRequest('/api/admin/users/resetpwd', { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify({ id, password: newPassword }) 
    });
    if (data.success) {
      message.success(`密码重置成功！新密码：${newPassword}（已复制到剪贴板）`);
      // 复制密码到剪贴板
      navigator.clipboard.writeText(newPassword).catch(err => {
        console.error('复制到剪贴板失败:', err);
      });
    } else {
      message.error(data.error || '重置失败');
    }
  };

  return {
    users, userForm, userModalVisible, setUserModalVisible, editingUser, selectedUserKeys, setSelectedUserKeys,
    userSearch, setUserSearch, resetPwd,
    fetchUsers, handleAddUser, handleEditUser, handleDeleteUser, handleResetPwd, handleSaveUser,
    brandingScopeModalVisible, brandingScopeUserId, brandingScopeUserName,
    openBrandingScopeModal, closeBrandingScopeModal, apiRequest
  };
}
